import React from "react";

import { CrossVersionPlatformNames } from "../../../../constants";
import { CrossVersionPlatforms } from "../../../../models/aaaGameConceptModels";
import { AaaGameInformationXboxNetworkDataType } from "../../../../models/forms";
import { XboxNetworkAddOns } from "../../../../models/xboxNetworkAddOns";
import { ReadOnlyFormItem } from "../appInfoModelsAndHelpers";
import { ReadOnlyForm } from "../ReadOnlyForm";

type ReadOnlyAaaXboxNetworkProps = {
  dataInForm: AaaGameInformationXboxNetworkDataType;
};

const AaaXboxNetworkConfig: Map<string, ReadOnlyFormItem> = new Map([
  [
    "addOns",
    {
      title: "anticipatedAddOnContent",
      mapper: (
        data: AaaGameInformationXboxNetworkDataType,
        localizedStrings: Map<string, string>
      ) =>
        data.addOns.map(element => {
          switch (element) {
            case XboxNetworkAddOns.inGameSubscriptions:
              return localizedStrings.get("aaaAddOnInGameSubscriptions");
            default:
              return localizedStrings.get(element);
          }
        }),
    },
  ],
  [
    "additionalContent",
    {
      title: "describeAdditionalContent",
    },
  ],
  [
    "xboxNetworkFeatures",
    {
      mapper: (
        data: AaaGameInformationXboxNetworkDataType,
        localizedStrings: Map<string, string>
      ) =>
        data.xboxNetworkFeatures.map(element => {
          switch (element) {
            case "smartDelivery":
              return localizedStrings.get("aaaSmartDeliveryLabel");
            default:
              return localizedStrings.get(element);
          }
        }),
    },
  ],
  [
    "xboxLiveSignIn",
    {
      title: "aaaXboxLiveSignInTitle",
      mapper: (
        data: AaaGameInformationXboxNetworkDataType,
        localizedStrings: Map<string, string>
      ) =>
        data.xboxLiveSignIn.map(platform => {
          switch (platform) {
            case "Other":
              return data.otherXboxLiveSignIn;
            case CrossVersionPlatforms.WindowsStore:
              return localizedStrings.get("microsoftStoreOnWindowsPlatform");
            default:
              return CrossVersionPlatformNames[platform];
          }
        }),
    },
  ],
  [
    "xboxAchievements",
    {
      title: "aaaXboxAchievementsTitle",
      mapper: (
        data: AaaGameInformationXboxNetworkDataType,
        localizedStrings: Map<string, string>
      ) =>
        data.xboxAchievements.map(platform => {
          switch (platform) {
            case "Other":
              return data.otherXboxAchievements;
            case CrossVersionPlatforms.WindowsStore:
              return localizedStrings.get("microsoftStoreOnWindowsPlatform");
            default:
              return CrossVersionPlatformNames[platform];
          }
        }),
    },
  ],
  [
    "xboxCrossPlay",
    {
      title: "aaaXboxCrossPlayTitle",
      mapper: (
        data: AaaGameInformationXboxNetworkDataType,
        localizedStrings: Map<string, string>
      ) =>
        data.xboxCrossPlay.map(platform => {
          switch (platform) {
            case "Other":
              return data.otherXboxCrossPlay;
            case CrossVersionPlatforms.WindowsStore:
              return localizedStrings.get("microsoftStoreOnWindowsPlatform");
            default:
              return CrossVersionPlatformNames[platform];
          }
        }),
    },
  ],
  [
    "xboxCrossSaveProgress",
    {
      title: "aaaXboxCrossSaveTitle",
      mapper: (
        data: AaaGameInformationXboxNetworkDataType,
        localizedStrings: Map<string, string>
      ) =>
        data.xboxCrossSaveProgress.map(platform => {
          switch (platform) {
            case "Other":
              return data.otherXboxCrossSaveProgress;
            case CrossVersionPlatforms.WindowsStore:
              return localizedStrings.get("microsoftStoreOnWindowsPlatform");
            default:
              return CrossVersionPlatformNames[platform];
          }
        }),
    },
  ],
  [
    "windowsCrossSaveProgress",
    {
      title: "aaaWindowsCrossSaveTitle",
      mapper: (data: AaaGameInformationXboxNetworkDataType) =>
        data.windowsCrossSaveProgress.map(platform =>
          platform === "Other"
            ? data.otherWindowsCrossSaveProgress
            : CrossVersionPlatformNames[platform]
        ),
    },
  ],
]);

export const ReadOnlyAaaXboxNetwork = ({ dataInForm }: ReadOnlyAaaXboxNetworkProps) => {
  const extraStringsToLocalize = [
    "anticipatedFeatures",
    "sharedAchievements",
    "xboxPlayAnywhere",
    "xboxPlayAnywhereSubtitle",
    "crossOwnership",
    "crossSave",
    "crossProgression",
    "aaaSmartDeliveryLabel",
    "addOns",
    "dlcPacks",
    "durables",
    "consumables",
    "freeAddOnContent",
    "microsoftStoreOnWindowsPlatform",
    "aaaAddOnInGameSubscriptions",
  ];
  return (
    <ReadOnlyForm
      formConfig={AaaXboxNetworkConfig}
      formData={dataInForm}
      extraStringsToLocalize={extraStringsToLocalize}
    />
  );
};
